import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _IconExternalLinkLine;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useState } from 'react';
import { Popover } from '@instructure/ui-popover';
import { CloseButton } from '@instructure/ui-buttons';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import { Link } from '@instructure/ui-link';
import { IconExternalLinkLine } from '@instructure/ui-icons';
import { useStyle } from '@instructure/emotion';
import { NutritionFacts, DataPermissionLevels } from "../index.js";
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
/**
---
category: components/AI Components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const AiInformation = ({
  title,
  data,
  trigger,
  fullscreenModals = false,
  dataPermissionLevelsModalLabel,
  dataPermissionLevelsTitle,
  dataPermissionLevelsData,
  dataPermissionLevelsCloseButtonText,
  dataPermissionLevelsCloseIconButtonScreenReaderLabel,
  dataPermissionLevelsCurrentFeature,
  dataPermissionLevelsCurrentFeatureText,
  nutritionFactsModalLabel,
  nutritionFactsTitle,
  nutritionFactsFeatureName,
  nutritionFactsData,
  nutritionFactsCloseButtonText,
  nutritionFactsCloseIconButtonScreenReaderLabel
}) => {
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  const styles = useStyle({
    generateStyle,
    generateComponentTheme,
    componentId: 'AiInformation',
    displayName: 'AiInformation'
  });
  return _jsx("div", {
    children: _jsx(Popover, {
      renderTrigger: trigger,
      isShowingContent: open,
      on: "click",
      screenReaderLabel: "Popover Dialog Example",
      shouldContainFocus: true,
      shouldReturnFocus: true,
      shouldCloseOnDocumentClick: true,
      offsetY: "1rem",
      onShowContent: () => setOpen(true),
      onHideContent: () => setOpen(false),
      children: _jsxs("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.aiInformation,
        children: [_jsxs("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.header,
          children: [_jsx(Heading, {
            level: "h2",
            aiVariant: "stacked",
            variant: "titleModule",
            children: title
          }), _jsx(CloseButton, {
            placement: "end",
            offset: "small",
            onClick: () => setOpen(false),
            screenReaderLabel: "Close"
          })]
        }), _jsx("div", {
          children: data.map(({
            featureName,
            privacyNoticeText,
            privacyNoticeUrl,
            permissionLevelText,
            permissionLevel,
            description,
            permissionLevelsModalTriggerText,
            modelNameText,
            modelName,
            nutritionFactsModalTriggerText
          }, index) => _jsxs("div", {
            children: [_jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.privacyNotice,
              children: _jsx(Link, {
                href: privacyNoticeUrl,
                renderIcon: _IconExternalLinkLine || (_IconExternalLinkLine = _jsx(IconExternalLinkLine, {})),
                iconPlacement: "end",
                variant: "standalone",
                target: "_blank",
                children: privacyNoticeText
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.featureName,
              children: _jsx(Heading, {
                level: "h3",
                variant: "titleCardRegular",
                children: featureName
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.permissionLevelText,
              children: _jsx(Heading, {
                level: "h4",
                variant: "label",
                children: permissionLevelText
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.permissionLevel,
              children: _jsxs(Text, {
                variant: "legend",
                children: [" ", permissionLevel, " "]
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.description,
              children: _jsxs(Text, {
                variant: "contentSmall",
                children: [" ", description, " "]
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.permissionLevelsModalTriggerText,
              children: _jsx(DataPermissionLevels, {
                fullscreen: fullscreenModals,
                title: dataPermissionLevelsTitle,
                currentFeatureText: dataPermissionLevelsCurrentFeatureText,
                currentFeature: dataPermissionLevelsCurrentFeature,
                closeIconButtonScreenReaderLabel: dataPermissionLevelsCloseIconButtonScreenReaderLabel,
                closeButtonText: dataPermissionLevelsCloseButtonText,
                modalLabel: dataPermissionLevelsModalLabel,
                triggerText: permissionLevelsModalTriggerText,
                data: dataPermissionLevelsData
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.modelNameText,
              children: _jsxs(Heading, {
                level: "h3",
                variant: "label",
                children: [' ', modelNameText, ' ']
              })
            }), _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.modelName,
              children: _jsxs(Text, {
                variant: "contentSmall",
                children: [" ", modelName, " "]
              })
            }), _jsx(NutritionFacts, {
              fullscreen: fullscreenModals,
              modalLabel: nutritionFactsModalLabel,
              title: nutritionFactsTitle,
              featureName: nutritionFactsFeatureName,
              closeButtonText: nutritionFactsCloseButtonText,
              closeIconButtonScreenReaderLabel: nutritionFactsCloseIconButtonScreenReaderLabel,
              triggerText: nutritionFactsModalTriggerText,
              data: nutritionFactsData
            }), data.length !== index + 1 ? _jsx("div", {
              css: styles === null || styles === void 0 ? void 0 : styles.divider
            }) : null]
          }, index))
        })]
      })
    })
  });
};
export default AiInformation;
export { AiInformation };